import React, { useState, useRef } from 'react';
import './ImageModal.css';

interface ImageModalProps {
    imageUrl: string;
    onClose: () => void;
}

const ImageModal: React.FC<ImageModalProps> = ({ imageUrl, onClose }) => {
    const [zoom, setZoom] = useState(1);
    const imgRef = useRef<HTMLImageElement>(null);
    const startTouchesRef = useRef<{ x: number; y: number }[] | null>(null);
    const initialZoomRef = useRef<number>(1);

    if (!imageUrl) {
        return null;
    }

    const handleTouchStart = (e: React.TouchEvent) => {
        if (e.touches.length === 2) {
            initialZoomRef.current = zoom;
            startTouchesRef.current = [
                { x: e.touches[0].clientX, y: e.touches[0].clientY },
                { x: e.touches[1].clientX, y: e.touches[1].clientY },
            ];
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (e.touches.length === 2 && startTouchesRef.current) {
            const currentTouches = [
                { x: e.touches[0].clientX, y: e.touches[0].clientY },
                { x: e.touches[1].clientX, y: e.touches[1].clientY },
            ];

            const startDist = Math.hypot(
                startTouchesRef.current[0].x - startTouchesRef.current[1].x,
                startTouchesRef.current[0].y - startTouchesRef.current[1].y
            );
            const currentDist = Math.hypot(
                currentTouches[0].x - currentTouches[1].x,
                currentTouches[0].y - currentTouches[1].y
            );

            const scale = currentDist / startDist;

            // Compute zoom relative to the zoom level at gesture start for natural pinch behavior
            // zoom = initialZoom * (currentDist / startDist) prevents exponential compounding
            const newZoom = initialZoomRef.current * scale;
            setZoom(Math.min(Math.max(newZoom, 1), 5)); // Clamp between 1 and 5
        }
    };

    const handleTouchEnd = () => {
        startTouchesRef.current = null;
    };

    const handleWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setZoom(prev => Math.min(Math.max(prev + delta, 1), 5));
    };

    return (
        <div className="image-modal-overlay" onClick={onClose}>
            <div className="image-modal-shell" onClick={(e) => e.stopPropagation()}>
                <button className="image-modal-close-button" onClick={onClose} aria-label="Close preview">
                    ×
                </button>
                <div
                    className="image-modal-frame"
                    style={{
                        overflow: zoom > 1 ? 'scroll' : 'hidden'
                    }}
                    onWheel={handleWheel}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <img
                        ref={imgRef}
                        src={imageUrl}
                        alt="Full size view"
                        style={{
                            transform: `scale(${zoom})`,
                            transformOrigin: 'center center'
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default ImageModal;
